/**
 * Packagings Tab Component
 *
 * Tab content for managing packagings in Settings page.
 * @see US-8.21 - Gestion des Conditionnements
 */

import { useState } from 'react';
import { Plus, Package, ToggleLeft, ToggleRight, Pencil } from 'lucide-react';
import { Button, Skeleton, Badge, EmptyState } from '@/components/ui';
import { formatQuantity } from '@/lib/format';
import {
  usePackagings,
  useDeactivatePackaging,
  useActivatePackaging,
} from '../../../packagings/hooks/usePackagings';
import { PackagingFormModal } from '../../../packagings/components/PackagingFormModal';
import type { PackagingWithUsageCount } from '@bstock/shared';

export function PackagingsTab() {
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [editingPackaging, setEditingPackaging] = useState<PackagingWithUsageCount | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { data: packagings, isLoading } = usePackagings(showInactive);
  const deactivateMutation = useDeactivatePackaging();
  const activateMutation = useActivatePackaging();

  const handleCreate = () => {
    setEditingPackaging(null);
    setIsFormModalOpen(true);
  };

  const handleEdit = (packaging: PackagingWithUsageCount) => {
    setEditingPackaging(packaging);
    setIsFormModalOpen(true);
  };

  const handleToggleStatus = async (packaging: PackagingWithUsageCount) => {
    if (packaging.isActive) {
      await deactivateMutation.mutateAsync(packaging.id);
    } else {
      await activateMutation.mutateAsync(packaging.id);
    }
  };

  const handleCloseModal = () => {
    setIsFormModalOpen(false);
    setEditingPackaging(null);
  };

  const activePackagings = packagings?.filter((p) => p.isActive) ?? [];
  const inactivePackagings = packagings?.filter((p) => !p.isActive) ?? [];

  return (
    <div className="space-y-6">
      {/* Actions */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-neutral-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
            />
            Afficher les inactifs
          </label>
        </div>

        <Button variant="primary" onClick={handleCreate}>
          <Plus className="w-4 h-4" />
          Nouveau conditionnement
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-24" />
          ))}
        </div>
      ) : activePackagings.length === 0 && inactivePackagings.length === 0 ? (
        <div className="bg-white border border-neutral-200 rounded-xl p-12 text-center">
          <EmptyState
            icon={Package}
            title="Aucun conditionnement cree"
            description="Les conditionnements permettent de standardiser les formats d'achat (Carton 24, Sac 25kg, etc.)"
            size="lg"
          />
          <Button variant="primary" className="mt-4" onClick={handleCreate}>
            <Plus className="w-4 h-4" />
            Creer mon premier conditionnement
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Active Packagings */}
          {activePackagings.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-neutral-700 mb-3">
                Conditionnements actifs ({activePackagings.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {activePackagings.map((packaging) => (
                  <PackagingCard
                    key={packaging.id}
                    packaging={packaging}
                    onEdit={handleEdit}
                    onToggleStatus={handleToggleStatus}
                    isToggling={
                      deactivateMutation.isPending || activateMutation.isPending
                    }
                  />
                ))}
              </div>
            </div>
          )}

          {/* Inactive Packagings */}
          {showInactive && inactivePackagings.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-neutral-500 mb-3">
                Conditionnements inactifs ({inactivePackagings.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {inactivePackagings.map((packaging) => (
                  <PackagingCard
                    key={packaging.id}
                    packaging={packaging}
                    onEdit={handleEdit}
                    onToggleStatus={handleToggleStatus}
                    isToggling={
                      deactivateMutation.isPending || activateMutation.isPending
                    }
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Form Modal */}
      <PackagingFormModal
        isOpen={isFormModalOpen}
        onClose={handleCloseModal}
        packaging={editingPackaging}
      />
    </div>
  );
}

interface PackagingCardProps {
  packaging: PackagingWithUsageCount;
  onEdit: (packaging: PackagingWithUsageCount) => void;
  onToggleStatus: (packaging: PackagingWithUsageCount) => void;
  isToggling: boolean;
}

function PackagingCard({ packaging, onEdit, onToggleStatus, isToggling }: PackagingCardProps) {
  const isActive = packaging.isActive;

  return (
    <div
      className={`
        bg-white border rounded-lg p-4 transition-all
        ${isActive ? 'border-neutral-200 hover:border-neutral-300' : 'border-neutral-100 bg-neutral-50 opacity-60'}
      `}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Package className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-neutral-400'}`} />
            <h4 className="font-medium text-neutral-900 truncate">{packaging.name}</h4>
          </div>
          <p className="text-sm text-neutral-500 mt-1">
            Contenu: {formatQuantity(packaging.quantity)} {packaging.unit.symbol}
          </p>
        </div>

        {packaging.usageCount > 0 && (
          <Badge variant="neutral">
            {packaging.usageCount} ref.
          </Badge>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-neutral-100">
        <Button
          variant="ghost"
          size="sm"
          className="p-1.5"
          onClick={() => onEdit(packaging)}
          title="Modifier"
        >
          <Pencil className="w-4 h-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className={`p-1.5 ${isActive ? 'text-success-600 hover:text-danger-600' : 'text-danger-600'}`}
          onClick={() => onToggleStatus(packaging)}
          disabled={isToggling}
          title={isActive ? 'Desactiver' : 'Reactiver'}
        >
          {isActive ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
